import React, { useEffect, useState } from "react";
import { RotateCcw, Ban } from "lucide-react";
import axios from "../../axios/AxiosInstance";
import { apiErrorMessage } from "../../utils/apiError";

export default function ClientProspectingStatus({ client, onReactivated }) {
  const id = client._id || client.client_id || client.id;
  const [status, setStatus] = useState(null);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [restored, setRestored] = useState(false);
  const [alreadyActive, setAlreadyActive] = useState(false);
  useEffect(() => {
    let active = true;
    setStatus(null); setEditing(false); setReason(""); setError(""); setRestored(false); setAlreadyActive(false);
    const load = () => axios.get(`commercial/clients/${id}/prospecting-status`).then(({ data }) => {
      if (active) setStatus(data);
    }).catch(() => {});
    load();
    window.addEventListener("focus", load);
    return () => { active = false; window.removeEventListener("focus", load); };
  }, [id]);
  const fallback = client.commercialFollowUp || client;
  const lost = [client.status, client.commercialStatus, fallback.status, fallback.commercialStatus].some(value => String(value || "").trim().toLocaleLowerCase("fr-FR") === "client perdu");
  const blocked = status ? status.blocked : !!(client.prospectingBlocked || fallback.prospectingExcluded || fallback.prospectingOpposition || fallback.engagementBlocked);
  const exclusionReason = status?.reason || client.prospectingBlockReason || fallback.exclusionReason || "";
  const opposition = !!fallback.prospectingOpposition || exclusionReason === "Opposition à la prospection";
  const canReactivate = !opposition && (status?.canReactivate || (lost && !blocked) || (!status && blocked));
  if (restored) return <p role="status" className="mt-3 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{alreadyActive ? "Ce client est déjà disponible dans la prospection. Aucun doublon n’a été créé." : "Client réintégré aux leads de prospection."}</p>;
  if (!blocked && !lost) return null;
  const date = status?.at || fallback.exclusionAt;
  const validDate = date && !Number.isNaN(new Date(date).getTime());
  const submit = async event => {
    event.preventDefault();
    if (!reason.trim() || busy) return;
    setBusy(true); setError("");
    try {
      await axios.post(`commercial/clients/${id}/reactivate`, { reason: reason.trim() });
      setStatus({ blocked: false }); setEditing(false); setRestored(true);
      onReactivated?.();
    } catch (failure) {
      if (failure.response?.status === 409) {
        try {
          const { data } = await axios.get(`commercial/clients/${id}/prospecting-status`);
          if (data?.blocked === false) {
            setStatus(data); setEditing(false); setAlreadyActive(true); setRestored(true);
            return;
          }
        } catch { /* Keep the conflict visible if the current state cannot be verified. */ }
      }
      setError(failure.response?.status === 404
        ? "La réintégration sera disponible après la mise à jour du serveur. Le client reste retiré des leads."
        : apiErrorMessage(failure, "Impossible de réintégrer ce client. Réessayez."));
    } finally { setBusy(false); }
  };
  return <section aria-label="Statut de prospection" className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><p className="mb-0 flex items-center gap-2 font-semibold"><Ban size={16} />{lost ? "Client perdu" : "Retiré des leads de prospection"}</p>
        <p className="mb-0 mt-1 text-xs">{status?.reason || client.prospectingBlockReason || fallback.exclusionReason || "Motif non renseigné"}{validDate && ` · Le ${new Date(date).toLocaleDateString("fr-FR")}`}</p>
      </div>
      {canReactivate && !editing && <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold hover:bg-amber-100"><RotateCcw size={14} />{lost ? "Réactiver ce client" : "Réintégrer aux leads"}</button>}
    </div>
    {opposition && <p className="mb-0 mt-2 text-xs">Opposition à la prospection : une levée explicite est nécessaire avant réintégration.</p>}
    {editing && <form onSubmit={submit} className="mt-3 border-t border-amber-200 pt-3">
      <label className="block text-xs font-medium">{lost ? "Motif de la réactivation" : "Motif de la réintégration"}<textarea required maxLength={1000} value={reason} onChange={event => setReason(event.target.value)} disabled={busy} rows={2} className="mt-2 block w-full rounded-lg border border-amber-200 bg-white p-2 text-sm text-slate-800" /></label>
      <p className="mt-2 text-xs">Le client redevient disponible pour la prospection. Les anciennes relances annulées restent dans l’historique.</p>
      <div className="mt-2 flex gap-2"><button type="submit" disabled={busy || !reason.trim()} className="rounded-lg bg-blue-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">{busy ? "Réintégration…" : "Confirmer la réintégration"}</button><button type="button" disabled={busy} onClick={() => { setEditing(false); setError(""); }} className="px-3 py-2 text-xs">Annuler</button></div>
    </form>}
    {error && <p role="alert" className="mb-0 mt-2 text-xs text-red-700">{error}</p>}
  </section>;
}
